import type { QueryClient } from '@tanstack/react-query';

export const PATIENT_DETAIL_QUERY_PREFIX = ['patients', 'detail'] as const;
export const PATIENT_LIST_QUERY_PREFIX = ['patients', 'list'] as const;
export const PATIENT_PICKER_QUERY_PREFIX = ['patient-picker'] as const;
export const PATIENT_BY_IDS_QUERY_PREFIX = ['patients', 'by-ids'] as const;
export const PATIENT_RELATIONS_QUERY_PREFIX = ['patient-relations'] as const;

/** Fields the create/update-patient functions return — enough to refresh name views. */
export type PatientCachePatch = {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  notes: string | null;
  cave?: string | null;
  tags: string[];
  status: string;
  marketing_consent_email: boolean;
  marketing_consent_sms: boolean;
  address: Record<string, unknown>;
  has_cpr: boolean;
  updated_at: string;
};

export function patientDisplayName(row: Pick<PatientCachePatch, 'first_name' | 'last_name'>): string {
  return `${row.first_name} ${row.last_name}`.trim();
}

export function shouldApplyPatientPatch(
  previousUpdatedAt: unknown,
  nextUpdatedAt: string,
): boolean {
  if (typeof previousUpdatedAt !== 'string' || previousUpdatedAt.length === 0) return true;
  const previousMs = Date.parse(previousUpdatedAt);
  const nextMs = Date.parse(nextUpdatedAt);
  if (Number.isNaN(previousMs) || Number.isNaN(nextMs)) return true;
  return nextMs >= previousMs;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isRecordArray(value: unknown): value is Array<Record<string, unknown>> {
  return Array.isArray(value) && value.every(isPlainRecord);
}

export function mergePatientDetailCache(
  previous: Record<string, unknown> | undefined,
  row: PatientCachePatch,
): Record<string, unknown> | undefined {
  if (!previous) return previous;
  if (!shouldApplyPatientPatch(previous.updated_at, row.updated_at)) return previous;
  return {
    ...previous,
    first_name: row.first_name,
    last_name: row.last_name,
    email: row.email,
    phone: row.phone,
    notes: row.notes,
    ...(row.cave !== undefined ? { cave: row.cave } : {}),
    tags: row.tags,
    status: row.status,
    marketing_consent_email: row.marketing_consent_email,
    marketing_consent_sms: row.marketing_consent_sms,
    address: row.address,
    has_cpr: row.has_cpr,
    updated_at: row.updated_at,
  };
}

export function mergePatientCollectionCache(
  previous: unknown,
  row: PatientCachePatch,
): unknown {
  if (!isRecordArray(previous)) return previous;
  return previous.map((item) => {
    if (item.id !== row.id) return item;
    if (!shouldApplyPatientPatch(item.updated_at, row.updated_at)) return item;
    return {
      ...item,
      first_name: row.first_name,
      last_name: row.last_name,
      email: row.email,
      phone: row.phone,
      ...(Object.prototype.hasOwnProperty.call(item, 'status') ? { status: row.status } : {}),
      ...(Object.prototype.hasOwnProperty.call(item, 'has_cpr') ? { has_cpr: row.has_cpr } : {}),
      ...(Object.prototype.hasOwnProperty.call(item, 'tags') ? { tags: row.tags } : {}),
      ...(Object.prototype.hasOwnProperty.call(item, 'updated_at') ? { updated_at: row.updated_at } : {}),
    };
  });
}

export function mergePatientRelationsCache(previous: unknown, row: PatientCachePatch): unknown {
  if (!isRecordArray(previous)) return previous;
  const targetName = patientDisplayName(row);
  return previous.map((item) => {
    if (item.to_patient_id !== row.id) return item;
    return {
      ...item,
      target_name: targetName,
      target_email: row.email,
      target_phone: row.phone,
      ...(Object.prototype.hasOwnProperty.call(item, 'target_patient_status')
        ? { target_patient_status: row.status }
        : {}),
    };
  });
}

export type ApplyPatientMutationExtras = {
  relations?: boolean;
  detail?: boolean;
};

async function cancelPatientMutationQueries(
  qc: QueryClient,
  row: PatientCachePatch,
  extras: ApplyPatientMutationExtras,
): Promise<void> {
  const pending = [
    qc.cancelQueries({ queryKey: PATIENT_LIST_QUERY_PREFIX }),
    qc.cancelQueries({ queryKey: PATIENT_PICKER_QUERY_PREFIX }),
    qc.cancelQueries({ queryKey: PATIENT_BY_IDS_QUERY_PREFIX }),
  ];
  if (extras.detail) {
    pending.push(qc.cancelQueries({ queryKey: [...PATIENT_DETAIL_QUERY_PREFIX, row.id] }));
  }
  if (extras.relations) {
    pending.push(qc.cancelQueries({ queryKey: PATIENT_RELATIONS_QUERY_PREFIX }));
  }
  await Promise.all(pending);
}

function patchPatientMutationCaches(
  qc: QueryClient,
  row: PatientCachePatch,
  extras: ApplyPatientMutationExtras,
): void {
  if (extras.detail) {
    qc.setQueryData(
      [...PATIENT_DETAIL_QUERY_PREFIX, row.id],
      (previous: Record<string, unknown> | undefined) => mergePatientDetailCache(previous, row),
    );
  }
  qc.setQueriesData({ queryKey: PATIENT_LIST_QUERY_PREFIX }, (previous) =>
    mergePatientCollectionCache(previous, row),
  );
  qc.setQueriesData({ queryKey: PATIENT_PICKER_QUERY_PREFIX }, (previous) =>
    mergePatientCollectionCache(previous, row),
  );
  qc.setQueriesData({ queryKey: PATIENT_BY_IDS_QUERY_PREFIX }, (previous) =>
    mergePatientCollectionCache(previous, row),
  );
  if (extras.relations) {
    qc.setQueriesData({ queryKey: PATIENT_RELATIONS_QUERY_PREFIX }, (previous) =>
      mergePatientRelationsCache(previous, row),
    );
  }
}

function invalidatePatientMutationCaches(
  qc: QueryClient,
  row: PatientCachePatch,
  extras: ApplyPatientMutationExtras,
): void {
  if (extras.detail) {
    void qc.invalidateQueries({ queryKey: [...PATIENT_DETAIL_QUERY_PREFIX, row.id] });
  }
  void qc.invalidateQueries({ queryKey: PATIENT_LIST_QUERY_PREFIX });
  void qc.invalidateQueries({ queryKey: PATIENT_PICKER_QUERY_PREFIX });
  void qc.invalidateQueries({ queryKey: PATIENT_BY_IDS_QUERY_PREFIX });
  if (extras.relations) {
    void qc.invalidateQueries({ queryKey: PATIENT_RELATIONS_QUERY_PREFIX });
  }
}

/**
 * Keep the header/list/picker/relations name in sync after create/update without
 * awaiting the background refetch (that wait was the slow Gemmer… path).
 * Cancel in-flight reads first so a stale refetch from the previous save cannot
 * overwrite this one.
 */
export async function applyPatientMutationToCache(
  qc: QueryClient,
  row: PatientCachePatch,
  extras: ApplyPatientMutationExtras = {},
): Promise<void> {
  await cancelPatientMutationQueries(qc, row, extras);
  patchPatientMutationCaches(qc, row, extras);
  invalidatePatientMutationCaches(qc, row, extras);
}
